import type { FC } from 'react';
import React from 'react';

import { FullscreenToggle } from '../FullscreenToggle/FullscreenToggle';
import { PictureInPictureToggle } from '../PictureInPictureToggle/PictureInPictureToggle';
import { PlayToggle } from '../PlayToggle/PlayToggle';
import { ProgressBar } from '../ProgressBar';
import { TimeDisplay } from '../TimeDisplay/TimeDisplay';
import { PlaybackRate } from '../PlaybackRate/PlaybackRate';
import { VolumeControl } from '../VolumeControl/VolumeControl';

import styles from './PlaybackControls.module.scss';

type PlaybackControlsProps = {
  currentTime: number;
  startTime: number;
  endTime: number;
  volume: number;
  playbackRate: number;
  isPlaying: boolean;
  hasAudio: boolean;
  isMuted: boolean;
  isFullscreen: boolean;
  isPictureInPicture: boolean;
  pictureInPictureSupported: boolean;
  fullscreenSupported: boolean;
  loadedPercentage: number;
  onPlay: () => void;
  onPause: () => void;
  onSeek: (newTime: number) => void;
  onMuteToggle: () => void;
  onVolumeChange: (volume: number) => void;
  onPlaybackRateChange: (playbackRate: number) => void;
  onEnterFullscreen: () => void;
  onExitFullscreen: () => void;
  onPictureInPictureToggle: () => void;
};

export const PlaybackControls: FC<PlaybackControlsProps> = ({
  currentTime,
  startTime,
  endTime,
  volume,
  playbackRate,
  isMuted,
  hasAudio,
  isPlaying,
  isFullscreen,
  isPictureInPicture,
  pictureInPictureSupported,
  fullscreenSupported,
  loadedPercentage,
  onPause,
  onPlay,
  onSeek,
  onMuteToggle,
  onVolumeChange,
  onPlaybackRateChange,
  onEnterFullscreen,
  onExitFullscreen,
  onPictureInPictureToggle,
}) => {
  const size = isFullscreen ? 'large' : 'medium';

  return (
    <div className={styles.container}>
      <ProgressBar
        size={size}
        currentTime={currentTime}
        startTime={startTime}
        endTime={endTime}
        loadedPercentage={loadedPercentage}
        onSeek={onSeek}
      />
      <div className={styles.controlsContainer}>
        <div className={styles.leftControls}>
          <div className={styles.leftControlsInner}>
            <PlayToggle
              size={size}
              isPlaying={isPlaying}
              onPause={onPause}
              onPlay={onPlay}
            />
            {hasAudio ? (
              <VolumeControl
                size={size}
                volume={volume}
                isMuted={isMuted}
                onMuteToggle={onMuteToggle}
                onVolumeChange={onVolumeChange}
              />
            ) : null}
          </div>

          <TimeDisplay
            currentTime={currentTime}
            startTime={startTime}
            endTime={endTime}
          />
        </div>

        <div className={styles.rightControls}>
          <PlaybackRate
            size={size}
            playbackRate={playbackRate}
            onPlaybackRateChange={onPlaybackRateChange}
          />
          {pictureInPictureSupported && (
            <PictureInPictureToggle
              size={size}
              isPictureInPicture={isPictureInPicture}
              onPictureInPictureToggle={onPictureInPictureToggle}
            />
          )}
          {fullscreenSupported && (
            <FullscreenToggle
              size={size}
              isFullscreen={isFullscreen}
              onEnterFullscreen={onEnterFullscreen}
              onExitFullscreen={onExitFullscreen}
            />
          )}
        </div>
      </div>
    </div>
  );
};
